'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import TopologyCanvas from './TopologyCanvas';
import TopologyNodeList from './TopologyNodeList';
import TopologyNodeDetail from './TopologyNodeDetail';
import TopologyControls from './TopologyControls';
import TopologyLegend from './TopologyLegend';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export type NodeStatus = 'online' | 'offline' | 'degraded' | 'maintenance';
export type NodeType = 'router' | 'switch' | 'server' | 'workstation' | 'ap' | 'firewall' | 'printer' | 'camera' | 'unknown';

export interface TopologyNode {
  id: string;
  label: string;
  type: NodeType;
  status: NodeStatus;
  ip: string;
  vlan: string;
  location: string;
  x: number;
  y: number;
  mac?: string;
  note?: string;
  device_id?: string;
}

export interface TopologyEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  speed: string;
  type: 'fiber' | 'copper' | 'wireless' | 'vpn';
}

export default function NetworkTopologyContent() {
  const [nodes, setNodes] = useState<TopologyNode[]>([]);
  const [edges, setEdges] = useState<TopologyEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<TopologyNode | null>(null);
  const [vlanFilter, setVlanFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showLabels, setShowLabels] = useState(true);
  const [showSpeeds, setShowSpeeds] = useState(false);
  const [layer, setLayer] = useState<'L2' | 'L3'>('L2');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [vlans, setVlans] = useState<string[]>([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);

  const fetchTopology = useCallback(async () => {
    setLoading(true);
    try {
      const [nodesRes, linksRes] = await Promise.all([
        supabase.from('topology_nodes').select('*').order('created_at'),
        supabase.from('topology_links').select('*'),
      ]);
      if (nodesRes.error) throw nodesRes.error;
      if (linksRes.error) throw linksRes.error;

      const mappedNodes: TopologyNode[] = (nodesRes.data || []).map((n: any) => ({
        id: n.id,
        label: n.label,
        type: n.tipo as NodeType,
        status: n.stato as NodeStatus,
        ip: n.ip_address || '',
        vlan: n.vlan || '',
        location: n.location || '',
        x: n.pos_x,
        y: n.pos_y,
        mac: n.mac_address,
        note: n.note,
        device_id: n.device_id,
      }));

      const mappedEdges: TopologyEdge[] = (linksRes.data || []).map((l: any) => ({
        id: l.id,
        source: l.source_id,
        target: l.target_id,
        label: l.label || '',
        speed: l.speed || '1G',
        type: l.tipo as 'fiber' | 'copper' | 'wireless' | 'vpn',
      }));

      setNodes(mappedNodes);
      setEdges(mappedEdges);

      const uniqueVlans = [...new Set(mappedNodes.map(n => n.vlan).filter(Boolean))];
      setVlans(uniqueVlans);
    } catch (err: any) {
      toast.error('Errore caricamento topologia: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTopology(); }, [fetchTopology]);

  const filteredNodes = nodes.filter((n) => {
    const matchVlan = vlanFilter === 'all' || n.vlan === vlanFilter;
    const matchStatus = statusFilter === 'all' || n.status === statusFilter;
    return matchVlan && matchStatus;
  });

  const filteredEdges = edges.filter(
    (e) =>
      filteredNodes.some((n) => n.id === e.source) &&
      filteredNodes.some((n) => n.id === e.target)
  );

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.15, 2.5));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.15, 0.3));
  const handleResetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleSaveSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      const snapshotData = {
        nodes: nodes.map(n => ({ id: n.id, label: n.label, type: n.type, status: n.status, ip: n.ip, vlan: n.vlan, x: n.x, y: n.y })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label, speed: e.speed, type: e.type })),
        nodes_count: nodes.length,
        links_count: edges.length,
        timestamp: new Date().toISOString(),
      };
      const { error } = await supabase.from('topology_snapshots').insert({
        nome: `Snapshot ${new Date().toLocaleString('it-IT')}`,
        descrizione: `Snapshot automatico - ${nodes.length} nodi, ${edges.length} link`,
        snapshot_data: snapshotData,
      });
      if (error) throw error;
      toast.success('Snapshot topologia salvato');
    } catch (err: any) {
      toast.error('Errore salvataggio snapshot: ' + err.message);
    } finally {
      setSavingSnapshot(false);
    }
  };

  const handleUpdateNodePosition = async (nodeId: string, x: number, y: number) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, x, y } : n));
    await supabase.from('topology_nodes').update({ pos_x: x, pos_y: y }).eq('id', nodeId);
  };

  const onlineCount = nodes.filter(n => n.status === 'online').length;
  const offlineCount = nodes.filter(n => n.status === 'offline').length;
  const degradedCount = nodes.filter(n => n.status === 'degraded').length;

  return (
    <div className="h-[calc(100vh-56px)] lg:h-screen flex flex-col bg-background overflow-hidden">
      <Toaster position="bottom-right" theme="dark" richColors />

      {/* Topbar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-surface flex-shrink-0 gap-4 flex-wrap">
        <Breadcrumb items={[{ label: 'Topologia di Rete' }]} />
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-white">Mappa di Rete</h1>
            <p className="text-sm text-secondary-color">
              {loading ? 'Caricamento...' : `${filteredNodes.length} nodi · ${filteredEdges.length} connessioni`}
            </p>
          </div>
          {!loading && (
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1.5 text-green-400 font-semibold">
                <span className="w-2.5 h-2.5 rounded-full bg-green-400" />{onlineCount} online
              </span>
              <span className="flex items-center gap-1.5 text-amber-400 font-semibold">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />{degradedCount} degradati
              </span>
              <span className="flex items-center gap-1.5 text-red-400 font-semibold">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />{offlineCount} offline
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Layer toggle */}
          <div className="flex bg-surface-elevated border border-border rounded-lg p-0.5">
            {(['L2', 'L3'] as const).map((l) => (
              <button
                key={`layer-${l}`}
                onClick={() => setLayer(l)}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-all ${layer === l ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-secondary-color'}`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* VLAN filter */}
          <div className="relative">
            <select
              value={vlanFilter}
              onChange={(e) => setVlanFilter(e.target.value)}
              className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500/50 appearance-none pr-7 cursor-pointer"
            >
              <option value="all">Tutte le VLAN</option>
              {vlans.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
          </div>

          {/* Status filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500/50 appearance-none pr-7 cursor-pointer"
            >
              <option value="all">Tutti gli Stati</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="degraded">Degradato</option>
              <option value="maintenance">Manutenzione</option>
            </select>
            <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
          </div>

          <button
            onClick={() => setShowLabels(!showLabels)}
            className={`flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border transition-all ${showLabels ? 'bg-sky-500/15 border-sky-500/30 text-sky-400' : 'bg-surface-elevated border-border text-secondary-color hover:text-white'}`}
          >
            <Icon name="TagIcon" size={15} />
            Etichette
          </button>

          <button
            onClick={() => setShowSpeeds(!showSpeeds)}
            className={`flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border transition-all ${showSpeeds ? 'bg-sky-500/15 border-sky-500/30 text-sky-400' : 'bg-surface-elevated border-border text-secondary-color hover:text-white'}`}
          >
            <Icon name="BoltIcon" size={15} />
            Velocità
          </button>

          <button
            onClick={handleSaveSnapshot}
            disabled={savingSnapshot || loading}
            className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border border-border bg-surface-elevated text-secondary-color hover:text-white transition-all disabled:opacity-50"
          >
            <Icon name="CameraIcon" size={15} />
            Snapshot
          </button>

          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border border-border bg-surface-elevated text-secondary-color hover:text-white transition-all"
          >
            <Icon name={sidebarOpen ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={15} />
            {sidebarOpen ? 'Nascondi' : 'Mostra'} Pannello
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && (
          <div className="w-64 flex-shrink-0 border-r border-border bg-surface overflow-y-auto scrollbar-thin">
            <TopologyNodeList
              nodes={filteredNodes}
              selectedNode={selectedNode}
              onSelectNode={setSelectedNode}
            />
          </div>
        )}

        <div className="flex-1 relative overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-secondary-color text-sm font-semibold">Caricamento topologia...</p>
              </div>
            </div>
          ) : (
            <TopologyCanvas
              nodes={filteredNodes}
              edges={filteredEdges}
              selectedNode={selectedNode}
              onSelectNode={setSelectedNode}
              zoom={zoom}
              pan={pan}
              onPanChange={setPan}
              showLabels={showLabels}
              showSpeeds={showSpeeds}
            />
          )}

          <div className="absolute bottom-5 right-5 flex flex-col gap-1.5">
            <TopologyControls
              zoom={zoom}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={handleResetView}
            />
          </div>

          <div className="absolute bottom-5 left-5">
            <TopologyLegend />
          </div>
        </div>

        {selectedNode && (
          <div className="w-72 flex-shrink-0 border-l border-border bg-surface overflow-y-auto scrollbar-thin">
            <TopologyNodeDetail
              node={selectedNode}
              edges={filteredEdges}
              allNodes={filteredNodes}
              onClose={() => setSelectedNode(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}